<template>
  <div class="cms" :class="{ disabled }">
    <button type="button" class="cms-trigger" :disabled="disabled" @click="openPicker">
      <span class="cms-trigger-text" :class="{ placeholder: !summaryText }">
        {{ summaryText || placeholder }}
      </span>
      <span class="cms-chevron" aria-hidden="true">▾</span>
    </button>
    <div v-if="pickedLabels.length" class="cms-chips">
      <span v-for="txt in pickedLabels" :key="`chip-${txt}`" class="cms-chip">{{ txt }}</span>
    </div>

    <Teleport to="body">
      <div v-if="open" class="cms-mask" @click.self="closePicker">
        <div class="cms-dialog" role="dialog" :aria-label="title || placeholder" @click.stop>
          <div class="cms-head">
            <h4 class="cms-title">{{ title || placeholder || '请选择' }}</h4>
            <button type="button" class="cms-close" aria-label="关闭" @click="closePicker">×</button>
          </div>
          <div class="cms-search-wrap">
            <input v-model="keyword" class="cms-search" type="search" placeholder="请输入搜索内容" />
          </div>
          <div class="cms-body">
            <div v-for="cat in filteredCategories" :key="cat.value" class="cms-cat">
              <button type="button" class="cms-cat-head" @click="toggleExpand(cat.value)">
                <span class="cms-cat-main">
                  <span class="cms-cat-label">{{ cat.label }}</span>
                  <span v-if="cat.desc" class="cms-cat-desc">{{ cat.desc }}</span>
                </span>
                <span class="cms-cat-right">
                  <span v-if="selectedCount(cat) > 0" class="cms-badge">{{ selectedCount(cat) }}</span>
                  <span class="cms-arrow">{{ isExpanded(cat.value) ? '⌃' : '⌄' }}</span>
                </span>
              </button>
              <div v-if="isExpanded(cat.value)" class="cms-children">
                <button
                  v-for="child in cat.children"
                  :key="child.value"
                  type="button"
                  class="cms-child"
                  :class="{ 'cms-child--on': draftSelected.includes(child.value) }"
                  @click="toggleChild(child.value)"
                >
                  <span class="cms-child-label">{{ child.label }}</span>
                  <span class="cms-child-mark">{{ draftSelected.includes(child.value) ? '✓' : '○' }}</span>
                </button>
              </div>
            </div>
            <p v-if="!filteredCategories.length" class="cms-empty">无匹配项</p>
          </div>
          <div v-if="draftPickedLabels.length" class="cms-picked">
            <span class="cms-picked-prefix">已选：</span>
            <span v-for="txt in draftPickedLabels" :key="`picked-${txt}`" class="cms-picked-chip">{{ txt }}</span>
          </div>
          <div class="cms-foot">
            <button type="button" class="btn btn-edit" @click="closePicker">取消</button>
            <button type="button" class="btn btn-primary" @click="confirmPicker">确定</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  placeholder: { type: String, default: '请选择' },
  title: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'change'])

const open = ref(false)
const keyword = ref('')
const draftSelected = ref([])
const expandedMap = ref({})

const childLabelMap = computed(() => {
  const map = {}
  for (const cat of props.categories || []) {
    for (const c of cat.children || []) {
      map[String(c.value)] = String(c.label || c.value || '')
    }
  }
  return map
})

const pickedLabels = computed(() =>
  (props.modelValue || [])
    .map((v) => childLabelMap.value[String(v)] || String(v))
    .filter(Boolean),
)

const summaryText = computed(() => {
  const labels = pickedLabels.value
  if (!labels.length) return ''
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return `${labels[0]}、${labels[1]}`
  return `${labels[0]}、${labels[1]} 等 ${labels.length} 项`
})

const draftPickedLabels = computed(() =>
  (draftSelected.value || [])
    .map((v) => childLabelMap.value[String(v)] || String(v))
    .filter(Boolean),
)

const filteredCategories = computed(() => {
  const list = Array.isArray(props.categories) ? props.categories : []
  const kw = String(keyword.value || '').trim().toLowerCase()
  if (!kw) return list
  return list
    .map((cat) => {
      const selfHit = String(cat.label || '').toLowerCase().includes(kw)
      const children = (cat.children || []).filter((c) =>
        String(c.label || '').toLowerCase().includes(kw),
      )
      if (selfHit) return { ...cat, children: cat.children || [] }
      if (children.length) return { ...cat, children }
      return null
    })
    .filter(Boolean)
})

function isExpanded(catValue) {
  return !!expandedMap.value[catValue]
}

function toggleExpand(catValue) {
  expandedMap.value = {
    ...expandedMap.value,
    [catValue]: !isExpanded(catValue),
  }
}

function selectedCount(cat) {
  const set = new Set(draftSelected.value || [])
  return (cat.children || []).filter((c) => set.has(c.value)).length
}

function toggleChild(childValue) {
  const arr = Array.isArray(draftSelected.value) ? [...draftSelected.value] : []
  const idx = arr.indexOf(childValue)
  if (idx >= 0) arr.splice(idx, 1)
  else arr.push(childValue)
  draftSelected.value = arr
}

function resetDraft() {
  draftSelected.value = Array.isArray(props.modelValue) ? [...props.modelValue] : []
  const next = {}
  const categories = props.categories || []
  for (let idx = 0; idx < categories.length; idx += 1) {
    next[categories[idx].value] = idx === 0
  }
  expandedMap.value = next
  keyword.value = ''
}

function openPicker() {
  if (props.disabled) return
  resetDraft()
  open.value = true
}

function closePicker() {
  open.value = false
}

function confirmPicker() {
  const next = [...draftSelected.value]
  emit('update:modelValue', next)
  emit('change', next)
  closePicker()
}

watch(
  () => props.categories,
  () => {
    if (open.value) resetDraft()
  },
)
</script>

<style scoped>
.cms {
  width: 100%;
}
.cms.disabled {
  opacity: 0.65;
  pointer-events: none;
}
.cms-trigger {
  width: 100%;
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid #d0d7e2;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  box-sizing: border-box;
}
.cms-trigger:hover {
  border-color: #c5d3ee;
  box-shadow: 0 6px 18px rgba(47, 106, 246, 0.08);
}
.cms-trigger-text {
  flex: 1;
  text-align: left;
  color: #1d2939;
  font-size: 13px;
  line-height: 1.4;
}
.cms-trigger-text.placeholder {
  color: #98a2b3;
}
.cms-chevron {
  color: #667085;
  font-size: 14px;
  flex-shrink: 0;
}
.cms-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.cms-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef4ff;
  color: #2f6af6;
  font-size: 12px;
  line-height: 18px;
}
.cms-mask {
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.cms-dialog {
  width: min(560px, 96vw);
  max-height: min(82vh, 720px);
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.22);
}
.cms-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #edf0f5;
}
.cms-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}
.cms-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 24px;
  line-height: 1;
  color: #667085;
  cursor: pointer;
  border-radius: 8px;
}
.cms-close:hover {
  background: #f2f4f7;
}
.cms-search-wrap {
  padding: 10px 16px;
  border-bottom: 1px solid #f2f4f7;
}
.cms-search {
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 999px;
  padding: 0 14px;
  font-size: 13px;
  background: #f8fafc;
}
.cms-body {
  flex: 1;
  overflow: auto;
  min-height: 200px;
}
.cms-cat {
  border-bottom: 1px solid #f0f2f6;
}
.cms-cat-head {
  width: 100%;
  border: none;
  background: #fff;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  text-align: left;
}
.cms-cat-head:hover {
  background: #f8fafc;
}
.cms-cat-main {
  flex: 1;
  min-width: 0;
}
.cms-cat-label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #232f45;
}
.cms-cat-desc {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #667085;
}
.cms-cat-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.cms-badge {
  min-width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 11px;
  background: #d8efff;
  color: #2f6eff;
  font-size: 12px;
  font-weight: 600;
}
.cms-arrow {
  color: #5f6f89;
  font-size: 14px;
}
.cms-children {
  background: #f6f7f9;
}
.cms-child {
  width: 100%;
  border: none;
  background: transparent;
  min-height: 40px;
  padding: 8px 16px 8px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  text-align: left;
  border-top: 1px solid #eceff4;
}
.cms-child:hover,
.cms-child--on {
  background: #eef4ff;
}
.cms-child-label {
  font-size: 13px;
  color: #2f3e57;
}
.cms-child-mark {
  font-size: 16px;
  color: #2f6eff;
  flex-shrink: 0;
}
.cms-empty {
  padding: 24px 16px;
  text-align: center;
  color: #667085;
  font-size: 13px;
}
.cms-picked {
  border-top: 1px solid #edf0f5;
  padding: 10px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  background: #fff;
  max-height: 96px;
  overflow: auto;
}
.cms-picked-prefix {
  color: #334866;
  font-size: 12px;
}
.cms-picked-chip {
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef4ff;
  color: #2f6af6;
  font-size: 12px;
}
.cms-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #edf0f5;
}
.cms-foot .btn {
  min-width: 88px;
  height: 36px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
}
.cms-foot .btn-primary {
  background: #2f6af6;
  color: #fff;
  border: 1px solid #2f6af6;
}
.cms-foot .btn-edit {
  background: #fff;
  color: #26457d;
  border: 1px solid #bfc9db;
}
</style>
